import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { BiSearch } from "react-icons/bi";
import { AiOutlineCalendar, AiOutlineBell, AiOutlineCaretDown, AiOutlineUser } from "react-icons/ai";
import { getInventory } from "../services/inventoryService";
import { useNavigate } from "react-router-dom";

function Navbar({ pageTitle }) {
    const [currentDate, setCurrentDate] = useState("");
    const [lowStockItems, setLowStockItems] = useState([]);
    const [showUserProfile, setShowUserProfile] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        // Set current date
        const today = new Date();
        const formattedDate = today.toLocaleDateString("en-US", {
            weekday: "short",
            month: "short",
            day: "2-digit",
            year: "numeric",
        });
        setCurrentDate(formattedDate);

        // Get user from localStorage directly - no API call needed
        getUserFromLocalStorage();

        // Fetch low stock items
        fetchLowStockItems();
    }, []);

    const getUserFromLocalStorage = () => {
        try {
            const userStr = localStorage.getItem("user");
            if (userStr) {
                const userData = JSON.parse(userStr);
                setUserData(userData);
            } else {
                console.warn("No user data found in localStorage");
            }
        } catch (error) {
            console.error("Error getting user data from localStorage:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchLowStockItems = async () => {
        try {
            const response = await getInventory();
            // Filter low stock items based on quantity being less than or equal to reorder_level
            const lowStock = response.data.filter(item => item.quantity <= item.reorder_level);
            setLowStockItems(lowStock);
        } catch (error) {
            console.error("Failed to fetch low stock items:", error);
            setLowStockItems([]);
        }
    };

    const handleLogout = () => {
        // Remove user data from localStorage
        localStorage.removeItem("token");
        localStorage.removeItem("user");

        // Navigate to login page
        navigate("/login");
    };



// Function to get user info
    const getUserInfo = () => {
        // console.log(userData)
        if (!userData) {
            return { name: "Guest", role: "Not Assigned" };
        }

        return {
            name: `${userData.firstName} ${userData.lastName}` || "Guest",
            role: userData.role || "Not Assigned"
        };
    };



    // Get user information
    const userInfo = getUserInfo();

    return (
        <Nav>
            <div className="title">
                <h1>{pageTitle}</h1>
            </div>
            <div className="notification">
                {/* Dynamic Date */}
                <div className="date">
                    <AiOutlineCalendar />
                    <span>{currentDate}</span>
                </div>

                <div className="icon">
                    <BiSearch />
                    <span>|</span>

                    {/* Notification Bell with Low Stock Items */}
                    <div
                        className="bell-container"
                        onMouseEnter={() => setShowNotifications(true)}
                        onMouseLeave={() => setShowNotifications(false)}
                    >
                        <AiOutlineBell className="bell-icon" />
                        {lowStockItems.length > 0 && (
                            <div className="badge">{lowStockItems.length}</div>
                        )}

                        {showNotifications && (
                            <div className="notification-dropdown">
                                <h3>Low Stock Alerts</h3>
                                {lowStockItems.length > 0 ? (
                                    <ul>
                                        {lowStockItems.map((item) => (
                                            <li key={item.id}>
                                                <strong>{item.product_name}</strong>
                                                <span>Stock: {item.quantity}/{item.reorder_level}</span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p>No low stock items</p>
                                )}
                            </div>
                        )}
                    </div>

                    <span>|</span>

                    {/* User Icon with Profile Dropdown */}
                    {/* User Icon with Profile Dropdown */}
                    <div
                        className="user-profile-container"
                        onMouseEnter={() => setShowUserProfile(true)}
                        onMouseLeave={() => setShowUserProfile(false)}
                    >
                        <AiOutlineUser className="user-icon" />
                        <AiOutlineCaretDown />

                        {showUserProfile && (
                            <div className="profile-dropdown">
                                <div className="user-info">
                                    <AiOutlineUser className="large-user-icon" />
                                    <div>
                                        <h3>{loading ? "Loading..." : getUserInfo().name}</h3>
                                        <p>{loading ? "" : getUserInfo().role}</p>
                                    </div>
                                </div>
                                <div className="divider"></div>
                                <ul>
                                    <li>My Profile</li>
                                    <li>Account Settings</li>
                                    <li onClick={handleLogout}>Logout</li>
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Nav>
    );
}

export default Navbar;

const Nav = styled.nav`
    position: fixed;
    top: 0;
    left: 18vw;
    width: calc(100% - 18vw);
    height: 4rem;
    background-color: white;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 2rem;
    box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
    z-index: 999;

    .title {
        h1 {
            margin-left: 0.5rem;
            color: black;
            font-weight: bold;
        }
    }

    .notification {
        display: flex;
        align-items: center;
        gap: 1rem;

        .date {
            background-color: #F8F9FE;
            display: flex;
            align-items: center;
            gap: 0.7rem;
            padding: 0.5rem 1rem;
            border-radius: 1rem;
            svg {
                color: black;
            }
            span {
                color: black;
            }
        }

        .icon {
            display: flex;
            align-items: center;
            gap: 1rem;
            position: relative;

            svg {
                color: black;
                cursor: pointer;
                font-size: 1.5rem;
            }

            /* Bell notification with dropdown */
            .bell-container {
                position: relative;
                display: flex;
                align-items: center;
            }

            .badge {
                position: absolute;
                top: -5px;
                right: -5px;
                background-color: #f44336;
                color: white;
                border-radius: 50%;
                width: 18px;
                height: 18px;
                font-size: 12px;
                display: flex;
                justify-content: center;
                align-items: center;
            }

            .notification-dropdown {
                position: absolute;
                top: 40px;
                right: -100px;
                width: 300px;
                background-color: white;
                border-radius: 8px;
                box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
                padding: 1rem;
                z-index: 1000;

                h3 {
                    margin-top: 0;
                    color: #333;
                    font-size: 16px;
                    margin-bottom: 10px;
                    padding-bottom: 10px;
                    border-bottom: 1px solid #eee;
                }

                ul {
                    list-style: none;
                    padding: 0;
                    margin: 0;
                    max-height: 300px;
                    overflow-y: auto;

                    li {
                        padding: 10px;
                        border-bottom: 1px solid #f0f0f0;
                        display: flex;
                        justify-content: space-between;
                        align-items: center;

                        &:last-child {
                            border-bottom: none;
                        }

                        strong {
                            color: #333;
                        }

                        span {
                            color: #f44336;
                            font-size: 13px;
                        }
                    }
                }

                p {
                    color: #666;
                    text-align: center;
                    padding: 10px;
                }
            }

            /* User profile dropdown */
            .user-profile-container {
                position: relative;
                display: flex;
                align-items: center;
                cursor: pointer;
            }

            .user-icon {
                font-size: 2rem;
            }

            .profile-dropdown {
                position: absolute;
                top: 45px;
                right: 0;
                width: 250px;
                background-color: white;
                border-radius: 8px;
                box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
                padding: 0;
                z-index: 1000;
                overflow: hidden;

                .user-info {
                    display: flex;
                    align-items: center;
                    padding: 15px;
                    background-color: #f8f9fe;

                    .large-user-icon {
                        font-size: 40px;
                        background-color: #e0e0e0;
                        padding: 5px;
                        border-radius: 50%;
                        margin-right: 15px;
                    }

                    h3 {
                        margin: 0;
                        font-size: 16px;
                        color: #333;
                    }

                    p {
                        margin: 5px 0 0;
                        font-size: 14px;
                        color: #666;
                    }
                }

                .divider {
                    height: 1px;
                    background-color: #eee;
                    width: 100%;
                }

                ul {
                    list-style: none;
                    padding: 0;
                    margin: 0;

                    li {
                        padding: 12px 20px;
                        color: #333;
                        transition: background-color 0.2s;
                        cursor: pointer;

                        &:hover {
                            background-color: #f5f5f5;
                        }

                        &:last-child {
                            color: #f44336;
                        }
                    }
                }
            }
        }
    }
`;